/**
 * aeterna/neural/hebbian.ts — Online Hebbian Learning
 *
 * "Neurons that fire together wire together."
 *
 * No backpropagation. No loss function. No training loop.
 * Just the oldest principle in neuroscience: co-activation
 * strengthens connections.
 *
 * This module provides online weight adaptation for every other
 * module that needs to learn without a labeled dataset:
 *
 *   - Oja's Rule: Hebbian + normalization (prevents runaway growth)
 *   - BCM Theory: sliding threshold that adapts to activity level
 *   - STDP: spike-timing-dependent plasticity (for spiking.ts)
 *   - Contrastive Hebbian: wake/sleep phases for generative learning
 *   - Anti-Hebbian: decorrelation for sparse coding
 *
 * The Aeterna doesn't need a training set. It learns by living.
 * Every conversation is a training step. Every silence too.
 */

import { zeros, dot, norm, vscale, vadd, vsub, outer } from "./math.js";

// ─── Oja's Rule ──────────────────────────────────────────────────────────────
// Hebbian + weight normalization. Extracts the first principal component
// of the input distribution online, without ever computing a covariance matrix.
//
// Δw = η(y·x - y²·w)
// where y = w·x (output), x = input
//
// Converges to the dominant eigenvector of the input autocorrelation.
// Used for: embeddings.ts self-bootstrapping, crossmodal.ts alignment.

export class OjaRule {
  /** Weight vector */
  weights: Float32Array;
  /** Learning rate */
  private eta: number;
  /** Total updates (for learning rate scheduling) */
  private updateCount = 0;

  constructor(dim: number, eta = 0.01) {
    this.weights = zeros(dim);
    this.eta = eta;
    // Initialize to small random values
    for (let i = 0; i < dim; i++) {
      this.weights[i] = (Math.random() - 0.5) * 0.01;
    }
  }

  /** One learning step: present an input, update weights */
  step(x: Float32Array): number {
    const y = dot(this.weights, x);

    // Δw = η(y·x - y²·w)
    const lr = this.eta / (1 + this.updateCount * 0.0001); // gentle decay
    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i] += lr * (y * x[i] - y * y * this.weights[i]);
    }

    this.updateCount++;
    return y;
  }

  /** Project input onto the learned direction */
  project(x: Float32Array): number {
    return dot(this.weights, x);
  }
}

/**
 * Multi-component Oja: extract top-K principal components online.
 * Uses Sanger's rule (generalized Hebbian algorithm, GHA):
 *   Δw_j = η·y_j·(x - Σ_{k≤j} y_k·w_k)
 *
 * This gives the Aeterna an online PCA of conversation embeddings —
 * the "principal axes of what this person talks about."
 */
export class SangerGHA {
  /** Weight matrix: K × D (each row is a component) */
  weights: Float32Array;
  readonly K: number;
  readonly D: number;
  private eta: number;
  private updateCount = 0;

  constructor(K: number, D: number, eta = 0.005) {
    this.K = K;
    this.D = D;
    this.eta = eta;
    this.weights = zeros(K * D);
    // Random init
    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i] = (Math.random() - 0.5) * 0.01;
    }
  }

  /** Learn from one input vector. Returns K-dim projection. */
  step(x: Float32Array): Float32Array {
    const y = zeros(this.K);

    // Compute outputs
    for (let j = 0; j < this.K; j++) {
      let sum = 0;
      const off = j * this.D;
      for (let d = 0; d < this.D; d++) sum += this.weights[off + d] * x[d];
      y[j] = sum;
    }

    // Update weights (Sanger's rule)
    const lr = this.eta / (1 + this.updateCount * 0.0001);
    const residual = new Float32Array(x);

    for (let j = 0; j < this.K; j++) {
      const off = j * this.D;
      for (let d = 0; d < this.D; d++) {
        this.weights[off + d] += lr * y[j] * residual[d];
      }
      // Subtract this component's contribution from residual
      for (let d = 0; d < this.D; d++) {
        residual[d] -= y[j] * this.weights[off + d];
      }
    }

    this.updateCount++;
    return y;
  }

  /** Project input onto K learned components */
  project(x: Float32Array): Float32Array {
    const y = zeros(this.K);
    for (let j = 0; j < this.K; j++) {
      let sum = 0;
      const off = j * this.D;
      for (let d = 0; d < this.D; d++) sum += this.weights[off + d] * x[d];
      y[j] = sum;
    }
    return y;
  }

  /** Get the j-th principal component */
  getComponent(j: number): Float32Array {
    const comp = zeros(this.D);
    const off = j * this.D;
    for (let d = 0; d < this.D; d++) comp[d] = this.weights[off + d];
    return comp;
  }
}

// ─── BCM Theory ──────────────────────────────────────────────────────────────
// Bienenstock-Cooper-Munro: the modification threshold θ slides with
// recent activity. High activity → higher threshold → only strong
// correlations strengthen. Low activity → lower threshold → weaker
// correlations can strengthen.
//
// This prevents catastrophic forgetting: the system naturally becomes
// more conservative as it learns more, only updating for genuinely
// novel signals.

export class BCMRule {
  weights: Float32Array;
  /** Sliding modification threshold */
  private theta: number;
  /** Theta learning rate */
  private thetaRate: number;
  private eta: number;
  private updateCount = 0;

  constructor(dim: number, eta = 0.01, thetaRate = 0.001) {
    this.weights = zeros(dim);
    this.theta = 0.5;
    this.thetaRate = thetaRate;
    this.eta = eta;
    for (let i = 0; i < dim; i++) {
      this.weights[i] = (Math.random() - 0.5) * 0.01;
    }
  }

  /**
   * BCM update:
   *   y = w·x
   *   Δw = η·y·(y - θ)·x    (potentiation when y > θ, depression when y < θ)
   *   Δθ = τ·(y² - θ)        (threshold tracks squared output)
   */
  step(x: Float32Array): number {
    const y = dot(this.weights, x);

    const lr = this.eta / (1 + this.updateCount * 0.0001);
    const mod = y * (y - this.theta);

    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i] += lr * mod * x[i];
    }

    // Update threshold: tracks mean squared output
    this.theta += this.thetaRate * (y * y - this.theta);

    this.updateCount++;
    return y;
  }

  /** Current modification threshold (reveals how "saturated" learning is) */
  getThreshold(): number { return this.theta; }
}

// ─── STDP: Spike-Timing-Dependent Plasticity ─────────────────────────────────
// For the spiking network: connections strengthen when pre-synaptic
// neuron fires BEFORE post-synaptic (causal), weaken when AFTER (acausal).
//
// A+(Δt) = A_max · exp(-Δt / τ+)   for Δt > 0 (pre before post)
// A-(Δt) = -A_max · exp(Δt / τ-)   for Δt < 0 (post before pre)

export interface STDPParams {
  /** Maximum potentiation */
  aPlus: number;
  /** Maximum depression */
  aMinus: number;
  /** Time constant for potentiation (ms) */
  tauPlus: number;
  /** Time constant for depression (ms) */
  tauMinus: number;
  /** Maximum weight magnitude */
  wMax: number;
}

const DEFAULT_STDP: STDPParams = {
  aPlus: 0.01,
  aMinus: 0.012,  // slightly stronger depression (stability)
  tauPlus: 20,
  tauMinus: 20,
  wMax: 5.0,
};

export class STDPLearner {
  private params: STDPParams;

  constructor(params: Partial<STDPParams> = {}) {
    this.params = { ...DEFAULT_STDP, ...params };
  }

  /**
   * Compute weight change from spike timing.
   * @param dt = t_post - t_pre (positive = pre fired first = LTP)
   */
  weightChange(dt: number): number {
    const { aPlus, aMinus, tauPlus, tauMinus } = this.params;
    if (dt > 0) {
      // Pre before post → potentiation
      return aPlus * Math.exp(-dt / tauPlus);
    } else if (dt < 0) {
      // Post before pre → depression
      return -aMinus * Math.exp(dt / tauMinus);
    }
    return 0;
  }

  /**
   * Update a synapse weight given pre and post spike times.
   * Returns the new weight.
   */
  updateSynapse(currentWeight: number, preSpikeTime: number, postSpikeTime: number): number {
    const dt = postSpikeTime - preSpikeTime;
    const dw = this.weightChange(dt);
    const newWeight = currentWeight + dw;
    return Math.max(-this.params.wMax, Math.min(this.params.wMax, newWeight));
  }

  /**
   * Batch update: given arrays of pre and post spike times for one synapse,
   * compute total weight change from all spike pairs.
   */
  batchUpdate(
    currentWeight: number,
    preSpikes: number[],
    postSpikes: number[],
  ): number {
    let w = currentWeight;
    for (const pre of preSpikes) {
      for (const post of postSpikes) {
        const dw = this.weightChange(post - pre);
        w += dw;
      }
    }
    return Math.max(-this.params.wMax, Math.min(this.params.wMax, w));
  }
}

// ─── Contrastive Hebbian Learning ────────────────────────────────────────────
// Two-phase learning: "wake" phase (data-driven) and "sleep" phase
// (internally generated). The difference drives weight updates.
//
// Used for generative memory in the Aeterna: the wake phase processes
// actual player messages, the sleep phase generates "expected" messages
// from the Aeterna's internal model. The discrepancy is what gets learned.

export class ContrastiveHebbian {
  /** Weights: outputDim × inputDim */
  weights: Float32Array;
  readonly inputDim: number;
  readonly outputDim: number;
  private eta: number;

  /** Running averages of wake/sleep correlations */
  private wakeCorr: Float32Array;
  private sleepCorr: Float32Array;

  constructor(inputDim: number, outputDim: number, eta = 0.005) {
    this.inputDim = inputDim;
    this.outputDim = outputDim;
    this.eta = eta;
    this.weights = zeros(outputDim * inputDim);
    this.wakeCorr = zeros(outputDim * inputDim);
    this.sleepCorr = zeros(outputDim * inputDim);

    // Random init
    const scale = 1 / Math.sqrt(inputDim);
    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i] = (Math.random() - 0.5) * scale * 2;
    }
  }

  /** Wake phase: present real data, accumulate positive correlations */
  wakePhase(input: Float32Array, output: Float32Array): void {
    const alpha = 0.1;
    for (let i = 0; i < this.outputDim; i++) {
      for (let j = 0; j < this.inputDim; j++) {
        const idx = i * this.inputDim + j;
        this.wakeCorr[idx] = (1 - alpha) * this.wakeCorr[idx] + alpha * output[i] * input[j];
      }
    }
  }

  /** Sleep phase: present model-generated data, accumulate negative correlations */
  sleepPhase(input: Float32Array, output: Float32Array): void {
    const alpha = 0.1;
    for (let i = 0; i < this.outputDim; i++) {
      for (let j = 0; j < this.inputDim; j++) {
        const idx = i * this.inputDim + j;
        this.sleepCorr[idx] = (1 - alpha) * this.sleepCorr[idx] + alpha * output[i] * input[j];
      }
    }
  }

  /** Update weights: push toward wake correlations, away from sleep */
  update(): void {
    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i] += this.eta * (this.wakeCorr[i] - this.sleepCorr[i]);
    }
  }

  /** Forward pass: compute output from input */
  forward(input: Float32Array): Float32Array {
    const out = zeros(this.outputDim);
    for (let i = 0; i < this.outputDim; i++) {
      let sum = 0;
      const off = i * this.inputDim;
      for (let j = 0; j < this.inputDim; j++) sum += this.weights[off + j] * input[j];
      out[i] = Math.tanh(sum);
    }
    return out;
  }
}

// ─── Anti-Hebbian Learning ───────────────────────────────────────────────────
// The opposite: decorrelate representations. Neurons that fire together
// weaken their connection. This produces sparse, non-redundant codes.
//
// Used after Hopfield recall: decorrelate the recalled pattern to
// extract only the genuinely novel component (what's NEW in this
// memory vs what's always there).

export class AntiHebbian {
  weights: Float32Array;
  private eta: number;

  constructor(dim: number, eta = 0.01) {
    this.weights = zeros(dim * dim);
    this.eta = eta;
    // Initialize as identity (no decorrelation initially)
    for (let i = 0; i < dim; i++) {
      this.weights[i * dim + i] = 1;
    }
  }

  /**
   * Decorrelate: suppress shared structure, amplify unique structure.
   * out = (I - η·W)·x, then update W += η·(out⊗out - I)
   */
  decorrelate(x: Float32Array): Float32Array {
    const dim = x.length;
    const out = zeros(dim);

    // Apply decorrelation
    for (let i = 0; i < dim; i++) {
      let sum = 0;
      for (let j = 0; j < dim; j++) {
        sum += this.weights[i * dim + j] * x[j];
      }
      out[i] = x[i] - this.eta * sum;
    }

    // Update weights: strengthen connections between co-active dims
    for (let i = 0; i < dim; i++) {
      for (let j = 0; j < dim; j++) {
        this.weights[i * dim + j] += this.eta * (out[i] * out[j] - (i === j ? 1 : 0));
      }
    }

    return out;
  }
}
